// import { canActivate, redirectUnauthorizedTo } from '@angular/fire/compat/auth-guard';
import {redirectLoggedInTo} from '@angular/fire/auth-guard';
import {AuthPipe} from '@angular/fire/auth-guard/auth-guard';
import {Route, Routes} from '@angular/router';
import {CanAccessHandoversGuard} from '@handover/guards/can-access-handovers.guard';
import {CanAccessMetersGuard} from '@meters/guards/can-access-meters.guard';
import {CanAccessMsaGuard} from '@msa/guards/can-access-msa.guard';
import {BannedToUpgradeGuard} from '@quota/guards/banned-to-upgrade.guard';
import {IsSubscriberCustomerGuard} from '@shared/guards/is-subscriber-customer.guard';
import {CanAccessUsersGuard} from '@users/guards/can-access-users.guard';
import {CanAccessVacanciesGuard} from '@vacancies/guards/can-access-vacancies.guard';
import {InitialDataResolver} from 'app/app.resolvers';
import {LayoutComponent} from 'app/layout/layout.component';
import {
    BillingManagementGuard
} from './modules/admin/subscriptions/guards/billing-management.guard';
import {JugBillingOnlyGuard} from './modules/admin/subscriptions/guards/jug-billing-only.guard';
import {PermissionsGuard} from './guards/permissions.guard';
import {TwoFactorRequiredGuard} from './guards/two-factor-required.guard';
import {CanAccessContractsGuard} from './modules/admin/contracts/guards/can-access-contracts.guard';
import {CanAccessLettersGuard} from './modules/admin/letters/guards/can-access-letters.guard';
import {vatAccessGuard} from './modules/admin/vat/guards/vat-access.guard';
import {VatExportFacadeService} from './modules/admin/vat/services/vat-export-facade.service';
import {VatComputeService} from './modules/admin/vat/services/vat-compute.service';
import {VatDataLoaderService} from './modules/admin/vat/services/vat-data-loader.service';
import {VatLandingDataService} from './modules/admin/vat/services/vat-landing-data.service';
import {PeriodsService} from '@periods/services/periods.service';
import {NahausTiersOptions, PermissionOptions} from './modules/admin/users/models/user.interface';
import {AccessLayerOptions} from "./modules/admin/booking/booking.routing";
import {DigitalProtocolAccessLayerOptions} from "./modules/admin/digital-protocol/digital-protocol.routing";
import {GraduatedRentService} from "./modules/admin/graduated-rent/services/graduated-rent.service";
import {provideTranslocoScope, TRANSLOCO_SCOPE} from "@ngneat/transloco";
import {LetterTemplatesService} from "@letters/services/letterTemplates.service";
import {TaskTagsService} from "./modules/admin/tasks/services/task-tags.service";
import {MsaService} from "@msa/services/msa.service";
import {MsaOldService} from "@msa/services";
import {MsaStatsService} from "@msa/services/msa-stats.service";
import {ContractsService} from "./modules/admin/contracts/services/contracts.service";
import {MsaResolver} from "@msa/resolvers/msa.resolver";
import {StatsMsaResolver} from "@msa/resolvers/stats-msa.resolver";
import {StatsService} from "./modules/admin/stats/services/stats.service";
import {TenantsService} from "./modules/admin/mietersuche/services/tenants.service";
import {TenantResolver} from "./modules/admin/mietersuche/resolvers/tenant.resolver";
import {TenantsModularService} from "./modules/admin/mietersuche/services/tenants-modular.service";
import {TenantsSearchActivitiesService} from "./modules/admin/mietersuche/services/tenants-search-activities.service";
import {CanDeactivateTenantDetails} from "./modules/admin/mietersuche/guards/tenants.guard";
import {UsersQuotaGuard} from "@users/guards/users-quota.guard";
import {TransactionsService} from "@transactions/services/transactions.service";
import {TransactionsModularService} from "@transactions/services/transactions-modular.service";
import {TransactionsActivitiesService} from "@transactions/services/transactions-activities.service";
import {TransactionResolver} from "@transactions/resolvers/transaction.resolver";
import {TransactionsStatsService} from "@transactions/services/transactions-stats.service";
import {BookingService} from "./modules/admin/booking/services/booking.service";
import {BookingPaymentsService} from "./modules/admin/booking/services/bookingPayments.service";
import {BoardService} from "./modules/admin/scrumboards/services/board.service";
import {NahausRemindersService} from "./modules/admin/reminders/services/nahausReminders.service";
import {NahausReminderModularService} from "./modules/admin/reminders/services/nahausReminders-modular.service";
import {ReminderResolver} from "./modules/admin/reminders/resolvers/reminder.resolver";
import {BoardsResolver} from "./modules/admin/scrumboards/resolvers/boards.resolver";
import {BoardResolver} from "./modules/admin/scrumboards/resolvers/board.resolver";
import {CardResolver} from "./modules/admin/scrumboards/resolvers/card.resolver";
import {BoardListResolver} from "./modules/admin/scrumboards/resolvers/list.resolver";
import {CardsService} from "./modules/admin/scrumboards/services/cards.service";
import {BoardOldService} from "./modules/admin/scrumboards/services/boardService.service";
import {ScrumboardImagesService} from "./modules/admin/scrumboards/services/scrumboard-images.service";
import {BoardsModularService} from "./modules/admin/scrumboards/services/boards-modular.service";
import {ContactsService} from "./modules/admin/contacts/services/contacts.service";
import {ContactsStatService} from "./modules/admin/contacts/services/contacts-stat.service";
import {StatContactsResolver} from "./modules/admin/contacts/resolvers/stat-contacts.resolver";
import {ContactResolver} from "./modules/admin/contacts/resolvers/contact.resolver";
import {ContactsTagsService} from "./modules/admin/contacts/services/contactsTags.service";
import {ContactsModularService} from "./modules/admin/contacts/services/contacts-modular.service";
import {SharedDefaultTagsService} from "./modules/admin/contacts/services/shared-default-tags.service";
import {ContractsModularService} from "./modules/admin/contracts/services/contracts-modular.service";
import {ContractsResolver} from "./modules/admin/contracts/resolvers/contracts-resolver.service";
import {MsaListForContractResolver} from "./modules/admin/contracts/resolvers/msa-list-for-contract.resolver";
import {CreateContractDraftGuard} from "./modules/admin/contracts/guards/create-contract-draft.guard";
import {DirtyCheckGuard} from "./modules/admin/contracts/guards/dirty-check.guard";
import {DirtyCheckGarageContractGuard} from "./modules/admin/contracts/guards/dirty-check-garage-contract.guard";
import {RegistrationStore} from "@meters/store/registration.store";
import {PropertyResolver} from "./modules/admin/properties/resolvers/property.resolver";
import {
    PropertyByIdFromParentDeep1Resolver
} from "./modules/admin/properties/resolvers/property-by-id-from-parent-deep1.resolver";
import {
    UnitsResolverDeep1Resolver
} from "./modules/admin/properties/features/units/resolvers/units-resolver-deep1.resolver";
import {ManagementByIDResolver} from "./modules/admin/managers/resolvers/managementByID.resolver";
import {
    PropertyPreviousPeriodResolver
} from "./modules/admin/properties/features/units/features/period/features/previous-period/resolvers/property-previous-period.resolver";
import {
    UnitPreviousPeriodResolver
} from "./modules/admin/properties/features/units/features/period/features/previous-period/resolvers/unit-previous-period.resolver";
import {
    PeriodPreviousPeriodResolver
} from "./modules/admin/properties/features/units/features/period/features/previous-period/resolvers/period-previous-period.resolver";
import {
    PropertyFormParentRouteResolver
} from "./modules/admin/properties/features/units/features/period/resolvers/property-form-parent-route.resolver";
import {
    UnitFromParentRouteResolver
} from "./modules/admin/properties/features/units/features/period/resolvers/unit-from-parent-route.resolver";
import {
    PeriodFromParentRouteResolver
} from "./modules/admin/properties/features/units/features/period/resolvers/period-from-parent-route.resolver";
import {TimeAgoPipe} from "ngx-moment";
import {ExpensesAccessLayerOptions} from 'app/modules/admin/expenses/expenses.routing';

import 'moment/locale/de';
import {registerLocaleData} from "@angular/common";
import localeDe from '@angular/common/locales/de';
import {Settings} from 'luxon'
import {
    eSignatureService
} from './modules/admin/digital-protocol/components/signature-frame/services/eSignatures.service';
import {SignatureResolver} from './modules/admin/signature/resolvers/pdfFile.resolver';
import {DigitalProtocilResolver} from './modules/admin/digital-protocol/resolvers/protokol-resolver.service';
import {TemplateResolver} from '@contracts/resolvers/template-resolver.service';
import {TransactionRuleResolver} from "./modules/admin/transactionsRules/resolvers/transaction-rule.resolver";
import {
    TransactionsRulesModularService
} from "./modules/admin/transactionsRules/services/transactionsRules-modular.service";
import {TransactionsRulesService} from "./modules/admin/transactionsRules/services/transactionsRules.service";
import {
    TransactionsRulesDialogsService
} from "./modules/admin/transactionsRules/services/transactions-rules-dialogs.service";
import {
    TransactionsRulesActivitiesService
} from "./modules/admin/transactionsRules/services/transactions-rules-activities.service";
import {NoAuthGuard} from './core/guards/noAuth.guard';
import {AuthGuard} from "./core/guards/auth.guard";
import {IsMasterOrAdminUserGuard} from "./guards/is-master-or-admin-user.guard";
import {isDevEnvGuard} from "@shared/guards/is-dev-env.guard";
import {serviceContractsV2Guard} from '@shared/guards/service-contracts-feature.guard';
import {canAccessMailboxGuard} from '@shared/guards/can-access-mailbox.guard';
import {canAccessCallAgentGuard} from '@shared/guards/can-access-call-agent.guard';
import {canAccessTeamChatGuard} from '@shared/guards/can-access-team-chat.guard';
import {IsEnterpriseUserGuard} from "@shared/guards/is-enterprise-user.guard";
import { DataExportResolver } from './modules/admin/data-export/resolvers/data-export-resolver.service';

registerLocaleData(localeDe);

// Global
Settings.defaultLocale = 'de-DE'

const redirectLoggedInToMainApp = (): AuthPipe => redirectLoggedInTo(['immobilien']);

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

  // Redirect empty path to '/dashboards/project'
  { path: '', pathMatch: 'full', redirectTo: 'dashboards' },

  // Redirect signed in user to the '/dashboards/project'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboards' },

  // Öffentliche Gastseite: Beitritt zur Eigentümerversammlung (Schritt 4).
  //
  // Bewusst OHNE Guard und OHNE LayoutComponent: Eigentümer haben kein Konto.
  // `NoAuthGuard` wäre falsch (er würde angemeldete Verwalter aussperren, die den
  // Link prüfen), `AuthGuard` erst recht. Die Seite bringt ihr eigenes Layout mit
  // und hängt an keinem Dienst des angemeldeten Bereichs.
  {
    path: 'etv-beitritt/:token',
    title: 'Eigentümerversammlung beitreten',
    loadComponent: () => import('app/modules/etv-join/etv-join.component').then(m => m.EtvJoinComponent),
  },

  {
    path: 'mcp',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: 'connect',
        title: 'KI-Assistent verbinden',
        loadChildren: () => import('app/modules/auth/mcp-connect/mcp-connect.routing'),
      },
    ],
  },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: 'forgot-password',
        title: 'Passwort vergessen',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routing'),
      },
      {
        path: 'referral-sign-in',
        data: {ownsDocumentTitle: true}, // SEO-Seite setzt den Dokumenttitel selbst (SeoService)
        loadChildren: () => import('app/modules/auth/referral-sign-in/referral-sign-in.routing'),
      },
      {
        path: 'anmelden',
        data: {ownsDocumentTitle: true}, // SEO-Seite setzt den Dokumenttitel selbst (SeoService)
        loadChildren: () => import('app/modules/auth/sign-in/sign-in.routing'),
      },
      {
        path: 'registrieren',
        data: {ownsDocumentTitle: true}, // SEO-Seite setzt den Dokumenttitel selbst (SeoService)
        loadChildren: () => import('app/modules/auth/sign-up/sign-up.routing'),
        // data: {
        //   // This 👇 will be used as the page seo data
        //   NgaoxSeo: {
        //     title: 'Registrieren Sie sich jetzt bei Nahaus für eine effektive Immobilienverwaltung',
        //     description: 'Nahaus Immobilienverwaltung Software bietet eine leistungsstarke und sichere Immobiliencloud-Lösung. Verwalten Sie Ihre Immobilien einfach und effizient online und behalten Sie stets den Überblick. Registrieren Sie sich jetzt kostenlos!',
        //     siteName: 'Nahaus.de'
        //   }
        // }
      },
      {
        path: 'konto-geloescht',
        title: 'Konto gelöscht',
        loadChildren: () => import('app/modules/admin/pages/account-deleted/account-deleted.routing'),
      },
      // {
      //   path: 'unlock-session',
      //   loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.module').then(m => m.AuthUnlockSessionModule),
      // },
    ],
  },

  // Auth routes for authenticated users
  {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                title: 'E-Mail bestätigen',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
            },
            {
                path: 'abmelden',
                title: 'Abmelden',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.routing'),
            },
            {
                path: 'willkommen',
                title: 'Willkommen',
                loadChildren: () => import('app/modules/auth/welcome/welcome.routing'),
            },
            {
                path: 'fehler', children: [
                    {
                        // 426 - Upgrade required
                        path: '426',
                        title: 'Upgrade erforderlich',
                        loadChildren: () => import('app/modules/admin/pages/error/error-426/error-426.routing')
                    },
                    {
                        // 423 - 2FA required
                        path: '423',
                        title: 'Zwei-Faktor-Authentifizierung erforderlich',
                        loadChildren: () => import('app/modules/admin/pages/error/error-423-2fa-required/error-423.routing')
                    },
                    ]
                },
        ],
    },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      // {
      //   path: 'home',
      //   loadChildren: () => import('app/modules/landing/home/home.module').then(m => m.LandingHomeModule),
      //   canLoad: [ComingSoonGuard]
      // },
      // Pages without authentication
      {
        path: 'coming-soon',
        title: 'Demnächst',
        loadChildren: () => import('app/modules/admin/pages/coming-soon/coming-soon.routing')
      },
    ]
  },

  // Admin routes
  {
      path: '',
      canActivate: [AuthGuard, BannedToUpgradeGuard, TwoFactorRequiredGuard],
      canActivateChild: [AuthGuard, BannedToUpgradeGuard, TwoFactorRequiredGuard],
    // ...canActivate(redirectUnauthorizedToLogin),
    // ...canActivate(redirectUnverifiedEmailsToConfirm),
    component: LayoutComponent,
    resolve: {
      initialData: InitialDataResolver
    },
    children: [
      // SETTINGS
      {
        path: 'einstellungen',
        title: 'Einstellungen',
        loadChildren: () => import('app/modules/admin/settings/settings.routing'),
      },
      {
        path: 'abonnement',
        title: 'Abonnement',
        loadChildren: () => import('app/modules/admin/subscriptions/subscription.routing'),
        canLoad: [BillingManagementGuard],
        canActivate: [BillingManagementGuard],
      },
      // CORE FEATURES
      {
        path: 'dashboards',
        title: 'Dashboard',
        loadChildren: () => import('./modules/admin/dashboards/main/project.routing'),
          canActivate:[BannedToUpgradeGuard]
      },
      {
        path: 'immobilien',
        title: 'Immobilien',
        loadChildren: () => import('app/modules/admin/properties/properties.routing'),
        // canLoad: [BannedToUpgradeGuard, IsSubscriberCustomerGuard, PermissionsGuard],
        canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard, PermissionsGuard],
        // canActivateChild: [PermissionsGuard],
        data: {
          permission: PermissionOptions.PROPERTIES_READ
        },
          providers:[
              PropertyResolver,
              PropertyByIdFromParentDeep1Resolver,
              UnitsResolverDeep1Resolver,
              ManagementByIDResolver,
              PropertyPreviousPeriodResolver,
              UnitPreviousPeriodResolver,
              PeriodPreviousPeriodResolver,
              PropertyFormParentRouteResolver,
              UnitFromParentRouteResolver,
              PeriodFromParentRouteResolver,
              BoardService,
              TimeAgoPipe,
              BoardsModularService,
          ]
      },
      {
        path: 'eigentuemer',
        title: 'Eigentümer',
        // loadChildren: () => import('app/modules/admin/landlords/landlords.module').then(m => m.LandlordsModule),
        loadChildren: () => import('app/modules/admin/landlords/landlords.routing'),
        data: {
          permission: PermissionOptions.LANDLORDS_READ
        },
        // canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'verwalter', // managers
        title: 'Verwalter',
        loadChildren: () => import('app/modules/admin/managers/managers.routing'),
        canLoad: [BannedToUpgradeGuard], // IsOwnerOrAdminGuard
        canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard], // IsOwnerOrAdminGuard
      },

      // ############# VERMIETUNG #############

      // BASIC FEATURES
      {
        path: 'msa',
        redirectTo: 'mieterselbstauskunft'
      },
      {
        path: 'mieterselbstauskunft',
        title: 'Mieterselbstauskunft',
        loadChildren: () => import('app/modules/admin/msa/msa.routing'),
        data: { permission: PermissionOptions.MSA_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, CanAccessMsaGuard, PermissionsGuard],
        providers: [
            MsaService,
            MsaOldService,
            MsaStatsService,
            ContractsService,
            MsaResolver,
            StatsService,
            StatsMsaResolver,
            {provide: TRANSLOCO_SCOPE, useValue: 'msa'}
        ]
      },
      {
        path: 'mietersuche',
        title: 'Mietersuche',
        loadChildren: () => import('./modules/admin/mietersuche/tenants.routing'),
        data: { permission: PermissionOptions.TENANTS_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, PermissionsGuard],
          providers:[
              TenantsService,
              TenantResolver,
              TenantsModularService,
              TenantsSearchActivitiesService,
              CanDeactivateTenantDetails,
          ]
      },
      {
        path: 'leerstand',
        title: 'Leerstand',
        loadChildren: () => import('app/modules/admin/vacancies/vacancies.routing'),
        data: { permission: PermissionOptions.VACANCIES_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, CanAccessVacanciesGuard,PermissionsGuard],
          providers: [
              {provide: TRANSLOCO_SCOPE, useValue: 'vacancies'}
          ]
      },
      {
        path: 'ein-auszug',
        title: 'Ein/Auszüge',
        loadChildren: () => import('app/modules/admin/handover/handover.routing'),
        data: { permission: PermissionOptions.PERIODS_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard,CanAccessHandoversGuard, PermissionsGuard],
      },
      // PRO FEATURES
      {
        path: 'mietvertraege',
        title: 'Mietverträge',
        loadChildren: () => import('app/modules/admin/contracts/contracts.routing'),
        data: { permission: PermissionOptions.CONTRACTS_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard,CanAccessContractsGuard, PermissionsGuard],
          providers: [
            eSignatureService,
              ContractsService,
              ContractsModularService,
              ContractsResolver,
              MsaListForContractResolver,
              CreateContractDraftGuard,
              DirtyCheckGuard,
              DirtyCheckGarageContractGuard,
              DigitalProtocilResolver,
              TemplateResolver,
              {provide: TRANSLOCO_SCOPE, useValue: 'contracts'}
          ]

      },
      {
        path: 'anschreiben',
        title: 'Anschreiben',
        loadChildren: () => import('app/modules/admin/letters/letters.routing'),
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, CanAccessLettersGuard],
          providers: [GraduatedRentService,LetterTemplatesService, provideTranslocoScope('letters')]
        // canActivate: [BannedToUpgradeGuard,CanAccessLettersGuard],
      },

      // ############# BUCHHALTUNG #############
      // CORE FEATURES
      {
        path: 'bankkonten',
        title: 'Bankkonten',
        loadChildren: () => import('app/modules/admin/bankAccounts/bank-accounts.routing'),
        data: { permission: PermissionOptions.BANK_ACCOUNTS_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard, PermissionsGuard],
      },

      // ############# ADVANCED FEATURES #############

        // ENTERPRISE FEATURES
        {
          path: 'notifications',
          title: 'Benachrichtigungen',
          loadChildren: () => import('app/modules/admin/notifications/notifications.routing'),
          canActivate: [BannedToUpgradeGuard,IsSubscriberCustomerGuard],
        },
      {
        path: 'benutzer',
        title: 'Benutzer',
        loadChildren: () => import('app/modules/admin/users/users.routing'),
        data: { permission: PermissionOptions.USERS_READ },
        canLoad: [BannedToUpgradeGuard],
        canActivate: [BannedToUpgradeGuard, CanAccessUsersGuard, PermissionsGuard],
          providers: [UsersQuotaGuard]
      },
      {
        path: 'zaehler',
        title: 'Zähler',
        loadChildren: () => import('app/modules/admin/meters/meters.routing'),
        canLoad: [BannedToUpgradeGuard],
        canActivate:[BannedToUpgradeGuard, CanAccessMetersGuard, PermissionsGuard],
        data: { permission: PermissionOptions.METERS_READ ,
          accessLayer: AccessLayerOptions.ACCOUNT
        },
        providers: [
          RegistrationStore
        ]
      },
      // {
        // path: 'dienstleistungen',
        // loadChildren: () => import('app/modules/admin/serviceProviders/service-providers.routing'),
        // canLoad: [ComingSoonGuard]
      // },
      {
        path: 'versicherungen',
        title: 'Versicherungen',
        loadChildren: () => import('app/modules/admin/insurance/insurance.routing'),
        // canLoad: [ComingSoonGuard]
      },
      {
        path: 'servicevertraege',
        title: 'Serviceverträge',
        loadChildren: () => import('app/modules/admin/serviceContracts/service-contracts.routing'),
        canActivate: [
          BannedToUpgradeGuard,
          IsSubscriberCustomerGuard,
          serviceContractsV2Guard,
        ],
      },
      {
        path: 'files',
        title: 'Dateimanager',
        loadChildren: () => import('app/modules/admin/file-manager/file-manager.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'datenexport',
        title: 'Datenexport',
        loadChildren: () => import('app/modules/admin/data-export/data-export.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard, IsMasterOrAdminUserGuard],
        providers: [DataExportResolver]
      },
      {
        path: 'kontakte',
        title: 'Kontakte',
        loadChildren: () => import('app/modules/admin/contacts/contacts.routing'),
        canActivate :[BannedToUpgradeGuard,IsSubscriberCustomerGuard],
          providers:[
              ContactsService,
              ContactsStatService,
              StatContactsResolver,
              ContactResolver,
              ContactsTagsService,
              ContactsModularService,
              SharedDefaultTagsService,
              {provide: TRANSLOCO_SCOPE, useValue: 'contacts'},
          ]
      },
      {
        path: 'schluessel',
        title: 'Schlüssel',
        loadChildren: () => import('app/modules/admin/key-management/key-management.routing'),
        data: { permission: PermissionOptions.KEY_MANAGEMENT_READ },
        canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard, isDevEnvGuard, PermissionsGuard],
      },
        {
            path: 'mailbox',
            title: 'Mailbox',
            loadChildren: () => import('app/modules/admin/mailbox/mailbox.routing'),
            // canAccessMailboxGuard: erst Rollout (Dev/Beta/JUG), dann Tarif (ENTERPRISE; bei JUG tarifunabhängig)
            // → PRO landet auf /fehler/431?feature=mailbox
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard, canAccessMailboxGuard],
            data: {upgradeFeature: 'mailbox'}
        },
        {
            path: 'chat_intern',
            title: 'Team Chat',
            loadChildren: () => import('app/modules/admin/chat/chat.routes'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard, canAccessTeamChatGuard],
        },
        {
            path: 'whatsapp',
            title: 'WhatsApp',
            loadChildren: () => import('app/modules/admin/whatsapp/whatsapp.routes'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard, isDevEnvGuard],
        },
        {
            path: 'chat',
            title: 'Nahaus AI',
            loadChildren: () => import('app/modules/admin/chatAI/chat.routes'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },

      // {
      //   path: 'vermoegen',
      //   loadChildren: () => import('app/modules/admin/assets/assets.module').then(m => m.AssetsModule)
      // },

      // pricing
      {
        path: 'preise',
        title: 'Tarife',
        loadChildren: () => import('app/modules/admin/pages/pricing/modern/modern.routing'),
        canActivate: [JugBillingOnlyGuard],
      },
      {
        path: 'upgrade',
        title: 'Tarife',
        loadChildren: () => import('app/modules/admin/pages/pricing/modern/modern.routing'),
        canActivate: [JugBillingOnlyGuard],
      },
      {
        path: 'abonnement/bestaetigt/BASIC',
        redirectTo:'abonnement/bestaetigt/basic',
      },
      {
        path: 'abonnement/bestaetigt/basic',
        title: 'Abonnement bestätigt',
        loadChildren: () => import('app/modules/admin/pages/basic-subscription-succeeded/basic-subscription-succeeded.routing'),
      },
      {
        path: 'abonnement/bestaetigt/starter',
        title: 'Abonnement bestätigt',
        loadChildren: () => import('app/modules/admin/pages/basic-subscription-succeeded/basic-subscription-succeeded.routing'),
      },
      {
        path: 'abonnement/bestaetigt/PRO',
        redirectTo:'abonnement/bestaetigt/pro',
      },
      {
        path: 'abonnement/bestaetigt/pro',
        title: 'Abonnement bestätigt',
        loadChildren: () => import('app/modules/admin/pages/pro-subscription-succeeded/pro-subscription-succeeded.routing'),
      },
      {
        path: 'abonnement/bestaetigt/ENTERPRISE',
        redirectTo:'abonnement/bestaetigt/enterprise',
      },
      {
        path: 'abonnement/bestaetigt/enterprise',
        title: 'Abonnement bestätigt',
        loadComponent: () => import('app/modules/admin/pages/pro-subscription-succeeded/pro-subscription-succeeded.component').then(m => m.ProSubscriptionSucceededComponent),
        data: {
          expectedTier: NahausTiersOptions.ENTERPRISE,
          successText: 'Ihr Konto wurde erfolgreich auf ENTERPRISE umgestellt'
        }
      },
      {
        path: 'abonnement/zahlungsmethode-hinzugefuegt',
        title: 'Zahlungsmethode hinzugefügt',
        loadChildren: () => import('app/modules/admin/pages/payment-method-added/payment-method-added.routing'),
      },
      {
        path: 'vermieter-bescheinigung',
        title: 'Vermieterbescheinigung',
        loadChildren: () => import('app/modules/admin/landlord-certificate/landlord-certificate.routing'),
        canActivate :[BannedToUpgradeGuard,IsSubscriberCustomerGuard],
      },
        {
        path: 'support',
        title: 'Support',
        loadChildren: () => import('app/modules/admin/help-center/help-center.routing')
      },
        {
            path: 'faqs',
            title: 'FAQs',
            loadChildren: () => import('app/modules/admin/help-center/faqs/faqs.routing')
        },
      {
        path: 'staffelmiete',
        title: 'Staffelmiete',
        loadChildren: () => import('app/modules/admin/graduated-rent/graduated-rent.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'indexmiete',
        title: 'Indexmiete',
        loadChildren: () => import('app/modules/admin/index-rent/index-rent.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'transaktionen',
        title: 'Transaktionen',
        loadChildren: () => import('app/modules/admin/transactions/transactions.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
          providers:[
              TransactionsService,
              TransactionsModularService,
              TransactionsActivitiesService,
              TransactionResolver,
              TransactionsStatsService,
              BookingService,
              BookingPaymentsService,
          ]
      },
        {
            path: 'regelmanagement',
            title: 'Regelmanagement',
            loadChildren: () => import('app/modules/admin/transactionsRules/transactionsRules.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            providers:[
                TransactionRuleResolver,
                TransactionsRulesModularService,
                TransactionsRulesService,
                TransactionsRulesDialogsService,
                TransactionsRulesActivitiesService
            ]
        },
        {
            path: 'mietverhaeltnisse',
            title: 'Mietverhältnisse',
            loadChildren: () => import('app/modules/admin/root-periods/root-periods.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
      {
        path: 'aufgaben',
        title: 'Todo',
        loadChildren: () => import('app/modules/admin/tasks/tasks.routing'),
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
          providers: [
              TaskTagsService,
              {provide: TRANSLOCO_SCOPE, useValue: 'tasks'}
          ]
      },
      {
        path: 'buchungen',
        title: 'Sollstellung',
        loadChildren: () => import('app/modules/admin/booking/booking.routing'),
        data : {
            accessLayer: AccessLayerOptions.ACCOUNT
        },
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'buchungen/op',
        title: 'Offene Posten',
        loadChildren: () => import('app/modules/admin/booking/booking.routing'),
        data : {
            accessLayer: AccessLayerOptions.ACCOUNT
        },
        canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
      },
      {
        path: 'notes',
        title: 'Notizen',
        loadChildren: () => import('app/modules/admin/notes/notes.routing'),
          canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
          providers: [
              {provide: TRANSLOCO_SCOPE, useValue: 'notes'}
          ]
      },
      {
        path: 'signature',
        title: 'Digital Signieren',
        loadChildren: () => import('app/modules/admin/signature/signatureModule.routing'),
          canActivate :[BannedToUpgradeGuard, IsEnterpriseUserGuard],
          providers: [
              SignatureResolver,
              {provide: TRANSLOCO_SCOPE, useValue: 'notes'}
          ]
      },
      {
        path: 'identity',
        title: 'Identitätsprüfung',
        loadChildren: () => import('app/modules/admin/identity/identity.routing'),
        canActivate :[BannedToUpgradeGuard, IsEnterpriseUserGuard],
      },
        // {
        //     path: 'empfehlungsbonus',
        //     canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        //     loadChildren: () => import('app/modules/admin/pages/affiliates/affiliates.routing')
        // },
      {
         path: 'ticketing',
         title: 'Ticket-Boards',
         loadChildren: () => import('app/modules/admin/scrumboards/scrumboards.routing'),
          canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
          providers:[
              BoardService,
              CardsService,
              BoardOldService,
              ScrumboardImagesService,
              BoardsModularService,
              BoardsResolver,
              BoardResolver,
              CardResolver,
              BoardListResolver,
              {provide: TRANSLOCO_SCOPE, useValue: 'boards'},
          ]
      },
        {
            path: 'reminders',
            title: 'Erinnerungen',
            loadChildren: () => import('app/modules/admin/reminders/reminders.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            providers: [
                BoardService,
                NahausRemindersService,
                NahausReminderModularService,
                ReminderResolver,
                {provide: TRANSLOCO_SCOPE, useValue: 'reminders'},
            ]
        },
        {
            path: 'Telefon-Agent',
            title: 'Telefon-Agent',
            loadChildren: () => import('app/modules/admin/callAgent/callAgent.routing'),
            canActivate : [BannedToUpgradeGuard, IsSubscriberCustomerGuard, canAccessCallAgentGuard],
        },
        {
            path: 'Automatisierungen',
            title: 'Automatisierungen',
            loadChildren: () => import('app/modules/admin/automation/automation.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
        {
            path: 'messages',
            title: 'Postausgang',
            loadChildren: () => import('./modules/admin/outbox/outbox.routes'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard, IsEnterpriseUserGuard, PermissionsGuard],
            data: {
                permission: PermissionOptions.OUTBOX_READ,
                upgradeFeature: 'outbox'
            },
        },
        // {
        //     path: 'anleitungen',
        //     loadChildren: () => import('app/modules/admin/instructions/instructions.module').then(m => m.InstructionsModule)
        // },
        {
            path: 'ausgaben',
            title: 'Ausgaben',
            loadChildren: () => import('app/modules/admin/expenses/expenses.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            data : {
              accessLayer: ExpensesAccessLayerOptions.PERIOD
          },
        },
        {
            path: 'umsatzsteuer',
            title: 'Umsatzsteuer',
            loadChildren: () => import('app/modules/admin/vat/vat.routing'),
            canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard, PermissionsGuard, vatAccessGuard],
            data: {
                permission: PermissionOptions.BOOKINGS_READ
            },
            // Fassade für Plan 3 (Plan 4 A12) samt frischen stateful Engine-Services für den Kz-48-Nachweis
            providers: [PeriodsService, VatExportFacadeService, VatDataLoaderService, VatLandingDataService, VatComputeService, BookingService, BookingPaymentsService],
        },
        {
            path: 'buchungskonten',
            title: 'Buchungskonten',
            loadChildren: () => import('app/modules/admin/bookingsAccounts/bookingsAccounts.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
        {
            path: 'protokolle',
            title: 'Digitale Übergabeprotokolle',
            loadChildren: () => import('app/modules/admin/digital-protocol/digital-protocol.routing'),
            data : {
                accessLayer: DigitalProtocolAccessLayerOptions.ACCOUNT
            },
            providers: [
              DigitalProtocilResolver,
              {provide: TRANSLOCO_SCOPE, useValue: 'notes'}
          ],
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
        // Invoice
        {
            path: 'rechnungen',
            title: 'Rechnungswesen',
            loadChildren: () => import('app/modules/admin/invoice/invoice.routes'),
            canActivate: [BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            // children: []
        },
        {
            path: 'inventory',
            title: 'Inventarverwaltung',
            loadChildren: () => import('app/modules/admin/inventory/ecommerce.routes'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
        {
            path: 'historie',
            title: 'Historie',
            loadChildren: () => import('app/modules/admin/activities/activities.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            data : {
                accessLayer: AccessLayerOptions.ACCOUNT
            },
        },
        {
          path: 'kalender',
          title: 'Kalender',
          loadChildren: () => import('app/modules/admin/calendar/calendar.routing'),
          canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
        {
            path: 'api',
            title: 'API-Schnittstelle',
            loadChildren: () => import('app/modules/admin/api/api.routing'),
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
            // data: { permission: PermissionOptions.USERS_READ },
            // canLoad: [BannedToUpasgradeGuard],
            // canActivate: [BannedToUpgradeGuard, CanAccessUsersGuard, PermissionsGuard],
            // providers: [UsersQuotaGuard]
        },
        {
            path: 'mieterhoehung',
            title: 'Mieterhöhung',
            loadChildren: () => import('app/modules/admin/increaseRent/increase-rent.routing'),
            data : {
                accessLayer: AccessLayerOptions.ACCOUNT
            },
            canActivate :[BannedToUpgradeGuard, IsSubscriberCustomerGuard],
        },
      // Error
      {
        path: 'fehler', children: [
          {
            path: '401',
            title: 'Anmeldung erforderlich',
            loadChildren: () => import('app/modules/admin/pages/error/error-401/error-401.routing')
          },
          {
            path: '403',
            title: 'Zugriff verweigert',
            loadChildren: () => import('app/modules/admin/pages/error/error-403-access-denied/error-403.routing')
          },
          {
            path: '404',
            title: 'Seite nicht gefunden',
            loadChildren: () => import('app/modules/admin/pages/error/error-404/error-404.routing')
          },
          // {
          //   // 426 - Upgrade required
          //   path: '426',
          //   loadChildren: () => import('app/modules/admin/pages/error/error-426/error-426.routing')
          // },
          {
            // 427 - Upgrade required - landlords used up
            path: '427',
            title: 'Kontingent erreicht',
            loadChildren: () => import('app/modules/admin/pages/error/error-427-landlords-used-up/error-427-landlords-used-up.routing')
          },
          {
            // 428 - Upgrade required - properties used up
            path: '428',
            title: 'Kontingent erreicht',
            loadChildren: () => import('app/modules/admin/pages/error/error-428-properties-used-up/error-428-properties-used-up.routing')
          },
          {
            // 429 - Upgrade required - units used up
            path: '429',
            title: 'Kontingent erreicht',
            loadChildren: () => import('app/modules/admin/pages/error/error-429-units-used-up/error-429-units-used-up.routing')
          },
          {
            // 430 - Upgrade to PRO required - module is locked
            path: '430',
            title: 'Upgrade erforderlich',
            loadChildren: () => import('app/modules/admin/pages/error/error-430-upgrade-required/error-430-upgrade-required.routing')
          },
          {
            // 431 - Upgrade to ENTERPRISE required - module is locked
            path: '431',
            title: 'Upgrade erforderlich',
            loadChildren: () => import('app/modules/admin/pages/error/error-431-upgrade-to-enterprise/error-431-upgrade-to-enterprise.routing')
          },
          {
            // 432 - Upgrade required - users used up
            path: '432',
            title: 'Kontingent erreicht',
            loadChildren: () => import('app/modules/admin/pages/error/error-432-users-used-up/error-432-users-used-up.routing')
          },
          {
            path: '500',
            title: 'Fehler',
            loadChildren: () => import('app/modules/admin/pages/error/error-500/error-500.routing')
          }
        ]
      },


      // 404 & Catch all
      {
        path: '404-not-found',
        title: 'Seite nicht gefunden',
        pathMatch: 'full',
        loadChildren: () => import('app/modules/admin/pages/error/error-404/error-404.routing')
      },
      { path: '**', redirectTo: '404-not-found' }
    ]
  }
] as Routes;
